/**
 * ProIdSelector - Autocomplete component for Upercut users to select a Pro account
 * Only visible for Upercut users, hidden for Pro users
 * Features async search with pagination for better performance
 */

import React, { useCallback, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Autocomplete, TextField, Box, Typography, InputAdornment } from '@mui/material';
import { GET_PROS } from '@gql/pros';
import { useProId } from '@contexts/ProIdContext';
import { useAuth } from '@contexts/AuthContext';
import { useDebounce } from '@hooks/useDebounce';
import Image from '@components/Image';

interface Pro {
    _id: string;
    commercialName: string;
    email: string;
    brandLogo?: {
        url: string;
    };
}

interface ProOption {
    label: string;
    value: string;
    email: string;
    brandLogo?: {
        url: string;
    };
}

interface ProIdSelectorProps {
    placeholder?: string;
    required?: boolean;
    size?: 'small' | 'medium';
    disabled?: boolean;
    pageSize?: number; // Number of items to fetch per page
    onChange?: (proId: string | null, commercialName?: string) => void;
}

const AutoCompletePro: React.FC<ProIdSelectorProps> = ({
    placeholder = "Recherche par nom d'entreprise ou adresse e-mail...",
    required = false,
    size = 'medium',
    disabled = false,
    pageSize = 20,
    onChange,
}) => {
    const { isUpercut, isPro, isLoading: authLoading } = useAuth();
    const { selectedProId, setProId, isProIdRequired } = useProId();

    const [inputValue, setInputValue] = useState<string>('');
    const [options, setOptions] = useState<ProOption[]>([]);
    const [selectedOption, setSelectedOption] = useState<ProOption | null>(null);
    const debouncedInputValue = useDebounce(inputValue, 300);

    const { data, loading, error, refetch } = useQuery<{
        getPros: {
            docs: Pro[];
            meta: {
                totalDocs: number;
                limit: number;
                page: number;
                totalPages: number;
                hasNextPage: boolean;
                hasPrevPage: boolean;
            };
        };
    }>(GET_PROS, {
        skip: authLoading || !isUpercut,
        notifyOnNetworkStatusChange: true,
        variables: {
            query: '',
            pagination: {
                limit: pageSize,
                offset: 0,
            },
        },
    });

    // Fetch options when debounced input changes
    const fetchOptions = useCallback(
        async (query: string) => {
            try {
                const { data } = await refetch({
                    query: query || '',
                    pagination: {
                        limit: pageSize,
                        offset: 0,
                    },
                });

                const fetchedOptions = (data?.getPros?.docs || []).map(pro => ({
                    label: pro.commercialName || pro.email,
                    value: pro._id,
                    email: pro.email,
                    brandLogo: pro.brandLogo,
                }));

                setOptions(fetchedOptions);
            } catch (error) {
                console.error('Error fetching Pro accounts:', error);
            }
        },
        [refetch, pageSize],
    );

    // Effect to fetch options when search query changes
    useEffect(() => {
        if (isUpercut && !authLoading) {
            fetchOptions(debouncedInputValue);
        }
    }, [debouncedInputValue, fetchOptions, isUpercut, authLoading]);

    // Transform initial data to options
    useEffect(() => {
        if (data?.getPros?.docs) {
            const initialOptions = data.getPros.docs.map(pro => ({
                label: pro.commercialName || pro.email,
                value: pro._id,
                email: pro.email,
                brandLogo: pro.brandLogo,
            }));
            setOptions(initialOptions);
        }
    }, [data]);

    // Handle input change for search
    const handleInputChange = useCallback((_event: React.SyntheticEvent, value: string, reason: string) => {
        // Only update input value if user is typing (not when selecting)
        if (reason === 'input') {
            setInputValue(value);
        }
    }, []);

    const handleChange = useCallback(
        (_event: any, newValue: ProOption | null, reason: string) => {
            const newProId = newValue?.value || null;
            setSelectedOption(newValue);
            setProId(newProId);
            onChange?.(newProId, newValue?.label);
            // Update input value to match selection
            if (newValue) {
                setInputValue(newValue.label);
            } else {
                setInputValue('');
            }
        },
        [setProId, onChange],
    );

    const hasError = required && isProIdRequired && !selectedProId;

    // Don't render for Pro users
    if (isPro || !isUpercut) {
        return null;
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Autocomplete
                options={options}
                getOptionLabel={option => option.label}
                value={selectedOption}
                inputValue={inputValue}
                onChange={handleChange}
                onInputChange={handleInputChange}
                loading={loading}
                disabled={disabled}
                fullWidth
                size={size}
                loadingText={<div style={{ display: 'flex', alignItems: 'center' }}>Chargement...</div>}
                noOptionsText="Aucun compte Pro trouvé"
                isOptionEqualToValue={(option, value) => option.value === value.value}
                filterOptions={x => x}
                renderInput={params => (
                    <TextField
                        {...params}
                        placeholder={placeholder}
                        required={hasError}
                        error={hasError}
                        helperText={
                            hasError && size !== 'small'
                                ? 'Veuillez sélectionner un compte Pro pour continuer'
                                : error
                                ? 'Impossible de charger les comptes Pro'
                                : ''
                        }
                        variant="outlined"
                        aria-label="Sélectionner un compte Pro"
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: selectedOption ? (
                                <>
                                    <InputAdornment position="start">
                                        <Image
                                            width={24}
                                            height={24}
                                            variant="circle"
                                            src={selectedOption.brandLogo?.url || '/default-logo.png'}
                                            alt={selectedOption.label}
                                        />
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                </>
                            ) : (
                                params.InputProps.startAdornment
                            ),
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                        <Box flexDirection="row" display="flex" alignItems="center" gap={1}>
                            <Image
                                width={32}
                                height={32}
                                variant="circle"
                                src={option?.brandLogo?.url || '/default-logo.png'}
                                alt={option.label}
                            />
                            <Box>
                                <Typography variant="body1">{option.label}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {option?.email}
                                </Typography>
                            </Box>
                        </Box>
                    </li>
                )}
            />
        </Box>
    );
};

export default AutoCompletePro;
